import React, { useEffect } from 'react';

import { Box, Flex } from 'rebass';
import { useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { bool, func, object } from 'prop-types';

import { Formik, Form } from 'formik';
import { TextFieldStyled, ButtonStyled } from '@nike/nike-design-system-components';

import Page from '../common/Page';
import Spinner from '../../components/spinner/Spinner';
import ProductContractGroupsEditHeader from './ProductContractGroupsEditHeader';

import { validationSchema } from '../../admin/constants';
import { requestProductContractGroupById, updateProductContractGroup } from './actions';
import { getPendingStatus } from '../../util/requestReducer';
import { getProductContractGroupById } from './selectors';

import LinkedContractsAndAccounts from './LinkedContractsAndAccounts/LinkedContractsAndAccounts';

const ProductContractGroupsEdit = ({
  isFetching = false,
  isUpdating = false,
  selectedProductContractGroup,
  updateProductContractGroup,
  requestProductContractGroupById
}) => {
  const { productContractGroupId } = useParams();

  useEffect(async () => {
    // ensure contract group exists in state w/ pagination
    if (productContractGroupId && !selectedProductContractGroup) {
      await requestProductContractGroupById(productContractGroupId);
    }
  }, [productContractGroupId, selectedProductContractGroup]);

  const onSubmit = ({ name }) =>
    updateProductContractGroup({
      productContractGroupId,
      data: { name }
    });

  const preventEnterKeySubmit = keyPress => {
    if ((keyPress.charCode || keyPress.keyCode) === 13) {
      keyPress.preventDefault();
    }
  };

  return (
    <Page
      alwaysShowHeader
      title='Edit Product Contract Group'
      linkBack={{ url: '/productContractGroups', linkTitle: 'Product Contract Groups' }}
      noPadding
      noExtraPadding
      HeaderContent={<ProductContractGroupsEditHeader productContractGroup={selectedProductContractGroup} />}
    >
      {isFetching ? (
        <Spinner large />
      ) : (
        <Box pt='20px' px='30px'>
          {!!selectedProductContractGroup && (
            <Box width='100%'>
              <Formik
                enableReinitialize
                initialValues={selectedProductContractGroup || { name: '' }}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
              >
                {({ values: { name }, errors, touched, handleChange, handleBlur, handleSubmit, dirty }) => (
                  <Form onSubmit={handleSubmit} onKeyDown={preventEnterKeySubmit}>
                    <Flex alignItems='center'>
                      <Box width='45%' alignSelf='center'>
                        <TextFieldStyled
                          id='name'
                          name='name'
                          label='Product Contract Group Name'
                          value={name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={errors.name && touched.name}
                          errorMessage={errors.name}
                          inputProps={{
                            style: {
                              backgroundColor: 'white',
                              color: 'gray',
                              fontWeight: 'bold'
                            }
                          }}
                        />
                      </Box>
                      <Box pl='15px' pb='10px'>
                        <ButtonStyled
                          disabled={!dirty || isUpdating}
                          type='submit'
                          appearance='primary'
                          background='light'
                          size='small'
                          style={{ width: '180px' }}
                        >
                          {isUpdating ? (
                            <span>
                              <Spinner small />
                            </span>
                          ) : (
                            <span>Apply Changes</span>
                          )}
                        </ButtonStyled>
                      </Box>
                    </Flex>
                  </Form>
                )}
              </Formik>
              <LinkedContractsAndAccounts productContractGroup={selectedProductContractGroup} />
            </Box>
          )}
        </Box>
      )}
    </Page>
  );
};

ProductContractGroupsEdit.defaultProps = {
  selectedProductContractGroup: null
};

ProductContractGroupsEdit.propTypes = {
  selectedProductContractGroup: object,
  isFetching: bool.isRequired,
  updateProductContractGroup: func.isRequired,
  requestProductContractGroupById: func.isRequired,
  isUpdating: bool.isRequired
};

const mapStateToProps = (state, { match }) => {
  const productContractGroupId = match?.params?.productContractGroupId;

  return {
    isFetching: getPendingStatus(state, requestProductContractGroupById.event.name),
    isUpdating: getPendingStatus(state, updateProductContractGroup.event.name),
    selectedProductContractGroup: getProductContractGroupById(state, productContractGroupId)
  };
};

export default connect(mapStateToProps, {
  updateProductContractGroup,
  requestProductContractGroupById
})(ProductContractGroupsEdit);
